import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api";
import { inquiryId } from "../../lib/ids";
import { formatDateTime } from "../../lib/format";
import { Link } from "react-router-dom";

const AdminInquiriesPage = () => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["admin-inquiries"],
    queryFn: () => apiClient.frontend.ListInquiries({ PageSize: 100, PageOffset: 0 }),
  });

  const toggleHandled = useMutation({
    mutationFn: ({ id, handled }: { id: string; handled: boolean }) =>
      apiClient.frontend.SetInquiryHandled(id, { Handled: handled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-inquiries"] }),
  });

  const deleteInquiry = useMutation({
    mutationFn: (id: string) => apiClient.frontend.DeleteInquiry(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-inquiries"] }),
  });

  const inquiries = query.data?.Inquiries ?? [];

  return (
    <div>
      <h1 className="font-display text-3xl uppercase-track mb-2">Inquiries</h1>
      <p className="text-xs uppercase-track text-[var(--color-kj-text-light)] mb-10">
        {inquiries.length} total
      </p>

      {query.isLoading && <p className="text-sm">Loading…</p>}
      {query.isError && (
        <p className="text-sm text-[var(--color-kj-danger)]">Failed to load inquiries.</p>
      )}

      {inquiries.length === 0 && query.isSuccess && (
        <div className="text-center py-24 border border-dashed border-[var(--color-kj-border)]">
          <p className="font-display italic text-[var(--color-kj-text-light)]">
            No inquiries yet.
          </p>
        </div>
      )}

      <ul className="space-y-4">
        {inquiries.map((inq) => {
          const id = inquiryId(inq.ID);
          return (
            <li
              key={id}
              className={[
                "border border-[var(--color-kj-border)] p-6 transition-opacity",
                inq.Handled ? "opacity-60" : "",
              ].join(" ")}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <div>
                  <p className="font-display text-lg">{inq.Name}</p>
                  <a
                    href={`mailto:${inq.Email}`}
                    className="text-xs uppercase-track text-[var(--color-kj-accent)] hover:underline"
                  >
                    {inq.Email}
                  </a>
                </div>
                <p className="text-xs uppercase-track text-[var(--color-kj-text-light)]">
                  {formatDateTime(inq.CreatedAt)}
                </p>
              </div>

              {inq.ArtworkID && (
                <p className="text-xs uppercase-track text-[var(--color-kj-text-light)] mb-3">
                  About:{" "}
                  <Link
                    to={`/admin/artwork/${inq.ArtworkID as unknown as string}`}
                    className="underline"
                  >
                    {inq.ArtworkID as unknown as string}
                  </Link>
                </p>
              )}

              <p className="text-sm whitespace-pre-wrap leading-relaxed text-[var(--color-kj-text)]">
                {inq.Message}
              </p>

              <div className="mt-4 flex items-center gap-4 text-xs uppercase-track">
                <button
                  type="button"
                  onClick={() => toggleHandled.mutate({ id, handled: !inq.Handled })}
                  className="text-[var(--color-kj-text-light)] hover:text-[var(--color-kj-text)] cursor-pointer"
                >
                  {inq.Handled ? "Mark unhandled" : "Mark handled"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm("Delete this inquiry?")) deleteInquiry.mutate(id);
                  }}
                  className="text-[var(--color-kj-danger)] hover:underline cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AdminInquiriesPage;
